import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { Overrideable, Reservation, ReservationActivity } from '@lib/state';
import { FormContext, FormContextValues } from 'react-hook-form';
import { ManagerOverrideInput, RefundableDaysTextDisplay } from '..';

interface Props {
  reservation: Reservation;
  actionType: ReservationActivity;
  overrideable?: boolean; //determines if override input can be displayed
  overrideRequired?: boolean;
  form: FormContextValues;
}

export const RefundableConfirmationForm: React.FC<Props> = ({
  reservation,
  actionType,
  overrideable = false,
  overrideRequired = false,
  form,
}) => {
  const action = getAction(actionType);
  const refundableStayDays = reservation.refundableStayDays;
  const remainingStayDays = reservation.remainingStayDays;
  const hasPendingCharges = reservation.hasPendingCharges;
  const canRefundWithOverride = reservation.canRefundWithOverride;

  if (
    remainingStayDays === undefined ||
    refundableStayDays === undefined ||
    hasPendingCharges === undefined ||
    canRefundWithOverride === undefined
  )
    return null;

  const hasRemainingStayDays = remainingStayDays > 0;
  let override: Overrideable = 'unavailable';
  // overrideRequired when the reservation does not have FLEX
  // overridable is true when the confirmation is triggered via the management portal
  // canRefundWithOverride is true when the reservation can be refunded, but requires a manager override

  if (overrideRequired && canRefundWithOverride === true) {
    override = 'required';
  } else if (overrideRequired && canRefundWithOverride === false) {
    override = 'unavailable';
  } else if (remainingStayDays === 0) {
    override = 'unavailable';
  } else if (refundableStayDays === remainingStayDays) {
    override = 'unavailable';
  } else if (overrideRequired) {
    override = 'required';
  } else if (canRefundWithOverride === true) {
    override = 'optional';
  } else if (refundableStayDays < remainingStayDays && overrideable) {
    override = 'optional';
  }

  return (
    <FormContext {...form}>
      {hasRemainingStayDays && (
        <RefundableDaysTextDisplay
          pb={1}
          override={override}
          remainingStayDays={remainingStayDays}
          refundableStayDays={refundableStayDays}
          hasPendingCharges={hasPendingCharges}
          canRefundWithOverride={canRefundWithOverride}
        />
      )}

      {override !== 'unavailable' && <ManagerOverrideInput required={overrideRequired} />}
      <Box paddingTop={2}>
        <Typography data-testid={`confirmationActionDisplay:${reservation.recordNumber}`}>
          Are you sure you want to {action} reservation {reservation.recordNumber}?
        </Typography>
      </Box>
    </FormContext>
  );
};

function getAction(actionType: ReservationActivity) {
  switch (actionType) {
    case ReservationActivity.CancelReservation:
      return 'cancel';
    case ReservationActivity.CheckOut:
      return 'check out';
    default:
      return 'cancel';
  }
}
